/**
 * Odds formats, with no Zod in it.
 *
 * Reached from the browser as `@geni/contracts/odds-format`, for the reason
 * `money.ts` gives at length: the package root pulls Zod in behind it, and a
 * betting page has no business downloading a validation library to print +150.
 *
 * Everything inside Geni is decimal and stays decimal. The catalogue, the gate,
 * the price search, the slip and the ticket all speak one unit, and nothing in
 * this file changes that. It sits at the two edges where a person is involved:
 * how a price is *shown* to the bettor, and how a price the bettor *typed* is
 * read back. Between those two edges the format does not exist.
 *
 * Why the edges matter: a sportsbook lets the bettor pick how prices look, and
 * a page showing +150 beside a widget saying 2.50 reads as two different
 * prices for one bet. And a bettor used to American odds will ask for
 * "something around +150", which as a decimal is a 150-to-one long shot.
 */

/** Every format a sportsbook's "odds format" menu commonly offers. */
export const ODDS_FORMATS = [
  'decimal',
  'american',
  'fractional',
  'hongkong',
  'indonesian',
  'malaysian',
] as const;

export type OddsFormat = (typeof ODDS_FORMATS)[number];

/**
 * The words an operator's page might use for each format.
 *
 * Read generously for the same reason `normaliseSport` is: an operator who has
 * to look up our spelling to make a feature work will not use the feature.
 * "European", "EU" and "US" are what the menus themselves say.
 */
const SPELLINGS: Record<string, OddsFormat> = {
  decimal: 'decimal',
  dec: 'decimal',
  european: 'decimal',
  eu: 'decimal',
  american: 'american',
  us: 'american',
  usa: 'american',
  moneyline: 'american',
  fractional: 'fractional',
  fraction: 'fractional',
  uk: 'fractional',
  british: 'fractional',
  hongkong: 'hongkong',
  hk: 'hongkong',
  indonesian: 'indonesian',
  indo: 'indonesian',
  id: 'indonesian',
  malaysian: 'malaysian',
  malay: 'malaysian',
  my: 'malaysian',
};

/**
 * The format an operator named, or decimal.
 *
 * Decimal on anything unrecognised, including nothing at all, because that is
 * exactly what the widget showed before formats existed. A typo in an
 * attribute must cost the operator their preference, never the panel.
 */
export function normaliseOddsFormat(raw: string | null | undefined): OddsFormat {
  if (raw === null || raw === undefined) return 'decimal';
  const key = raw.toLowerCase().replace(/[\s_-]+/g, '');
  return SPELLINGS[key] ?? 'decimal';
}

/**
 * A price written in `format`, as a decimal price. Null when it cannot be one.
 *
 * Fractional takes the fraction already divided — 3/2 arrives as 1.5.
 *
 * The null cases are the values a format cannot produce: American odds between
 * −100 and +100, an Indonesian price strictly between −1 and +1, a Malaysian
 * one outside −1 to +1. Treating those as prices anyway is how "around 50" on
 * an American page would become an even-money bet.
 */
export function toDecimalOdds(value: number, format: OddsFormat): number | null {
  if (!Number.isFinite(value)) return null;

  switch (format) {
    case 'decimal':
      return value > 1 ? value : null;
    case 'american':
      if (value >= 100) return 1 + value / 100;
      if (value <= -100) return 1 + 100 / -value;
      return null;
    case 'fractional':
    case 'hongkong':
      return value > 0 ? 1 + value : null;
    case 'indonesian':
      if (value >= 1) return 1 + value;
      if (value <= -1) return 1 + 1 / -value;
      return null;
    case 'malaysian':
      if (value > 0 && value <= 1) return 1 + value;
      if (value < 0 && value >= -1) return 1 + 1 / -value;
      return null;
  }
}

/**
 * The denominators a bookmaker's fractional ladder actually uses.
 *
 * Found by rendering a real price: 2.95 came out as 33/17, which is within half
 * a percent and appears on no sportsbook anywhere. A UK bettor reads 8/13,
 * 8/15 and 10/11 at a glance and a seventeenth as a typo.
 */
const LADDER_DENOMINATORS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 20] as const;

/**
 * The simplest fraction a sportsbook would print.
 *
 * Bookmakers quote from ladders — 10/11, 5/6, 6/4, 9/4 — and a decimal fed
 * through exact arithmetic lands between their rungs. The smallest ladder
 * denominator that comes within half a percent is taken, because that is how
 * the ladders themselves are built: 1.83 is 5/6 and 1.91 is 10/11, not 9/10.
 * Only when none is that close does the closest one win.
 */
function nearestFraction(value: number): [number, number] {
  let best: [number, number] = [Math.max(1, Math.round(value)), 1];
  let bestError = Math.abs(value - best[0]);

  for (const denominator of LADDER_DENOMINATORS) {
    const numerator = Math.round(value * denominator);
    if (numerator < 1) continue;
    const error = Math.abs(value - numerator / denominator);
    if (error <= value * 0.005) {
      best = [numerator, denominator];
      break;
    }
    if (error < bestError - 1e-9) {
      best = [numerator, denominator];
      bestError = error;
    }
  }

  const divisor = greatestCommonDivisor(best[0], best[1]);
  return [best[0] / divisor, best[1] / divisor];
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

/** Two decimals in the bettor's own number format, with an explicit sign if asked. */
function twoDecimals(value: number, lang: string, signed: boolean): string {
  const body = (() => {
    try {
      return new Intl.NumberFormat(lang, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(value));
    } catch {
      // An unusual tag must not take the panel down.
      return Math.abs(value).toFixed(2);
    }
  })();

  if (value < 0) return `−${body}`;
  return signed ? `+${body}` : body;
}

/**
 * A decimal price as the bettor has chosen to see prices.
 *
 * Decimal renders exactly as the widget always has: two decimals in the
 * bettor's own number format. American is written without grouping, because
 * that is how every sportsbook prints it — +1200, never +1,200.
 *
 * The minus is U+2212, the typographic one, so a negative price never wraps
 * away from its digits and never reads as a hyphen.
 */
export function formatOdds(decimal: number, format: OddsFormat, lang: string): string {
  if (!Number.isFinite(decimal) || decimal <= 1) return twoDecimals(decimal, lang, false);
  const profit = decimal - 1;

  switch (format) {
    case 'decimal':
      return twoDecimals(decimal, lang, false);
    case 'american':
      return profit >= 1
        ? `+${Math.round(profit * 100)}`
        : `−${Math.round(100 / profit)}`;
    case 'fractional': {
      const [numerator, denominator] = nearestFraction(profit);
      return `${numerator}/${denominator}`;
    }
    case 'hongkong':
      return twoDecimals(profit, lang, false);
    case 'indonesian':
      return profit >= 1 ? twoDecimals(profit, lang, true) : twoDecimals(-1 / profit, lang, true);
    case 'malaysian':
      return profit <= 1 ? twoDecimals(profit, lang, false) : twoDecimals(-1 / profit, lang, false);
  }
}

/** One price-shaped thing a bettor wrote, and what it means as a decimal. */
interface WrittenPrice {
  /** The number as written, with its sign. A fraction arrives divided. */
  readonly raw: number;
  /** The same price as a decimal, or null if it cannot be a price here. */
  readonly decimal: number | null;
}

/**
 * Fractions first, then signed three-digit numbers, then any other number.
 *
 * The order is the reading. "3/2" must not be read as a 3 and a 2, and "−110"
 * must not be read as the number 110. Everything else is an ordinary number,
 * with a comma accepted as the decimal separator because "1,80" is how most of
 * the widget's languages write it.
 */
const WRITTEN_PRICE =
  /(?<![\d.,/])(\d{1,3})\s*\/\s*(\d{1,3})(?![\d.,/])|(?<![\w.,])([+\-−])\s?(\d{3,5})(?![\d.,])|(?<![\w.,/])([+\-−]?)(\d{1,4}(?:[.,]\d{1,3})?)(?![\d/])/gu;

function pricesWrittenIn(text: string, pageFormat: OddsFormat): WrittenPrice[] {
  const found: WrittenPrice[] = [];

  for (const match of text.matchAll(WRITTEN_PRICE)) {
    const [, numerator, denominator, americanSign, americanDigits, sign, digits] = match;

    if (numerator !== undefined && denominator !== undefined) {
      const bottom = Number(denominator);
      if (bottom === 0) continue;
      const raw = Number(numerator) / bottom;
      found.push({ raw, decimal: toDecimalOdds(raw, 'fractional') });
      continue;
    }

    if (americanSign !== undefined && americanDigits !== undefined) {
      const raw = (americanSign === '+' ? 1 : -1) * Number(americanDigits);
      found.push({ raw, decimal: toDecimalOdds(raw, 'american') });
      continue;
    }

    if (digits !== undefined) {
      const magnitude = Number(digits.replace(',', '.'));
      const raw = sign === '-' || sign === '−' ? -magnitude : magnitude;
      /**
       * An unsigned number is read in the page's format.
       *
       * This is the one reading that cannot be settled by the shape of what was
       * typed: 1.80 is 1.80 on a decimal page and 2.80 on a Hong Kong one. The
       * bettor wrote it looking at the page, so the page decides. A fractional
       * page still reads a plain number as decimal — nobody writes 1.5 meaning
       * six to four.
       */
      const reading = pageFormat === 'fractional' ? 'decimal' : pageFormat;
      found.push({ raw, decimal: toDecimalOdds(raw, reading) });
    }
  }

  return found;
}

/** Whether the model's number is this written price, as written or as a decimal. */
function refersTo(claimed: number, price: WrittenPrice): boolean {
  const close = (a: number, b: number): boolean => Math.abs(a - b) < 0.005;
  return (
    close(claimed, price.raw) ||
    close(claimed, Math.abs(price.raw)) ||
    (price.decimal !== null && close(claimed, price.decimal))
  );
}

/**
 * The price a bettor asked for, as a decimal, read from their own words.
 *
 * The model's number is a claim about the message, as it is everywhere else in
 * this pipeline, and the message is what settles it. A model asked about
 * "+150" may answer 150, or 2.5 having done the conversion itself; "−110" may
 * come back as −110, which the gate would then throw away as out of range. So
 * the claim is used only to find WHICH price in the message was meant, and the
 * message says what that price is worth.
 *
 * When nothing in the message matches the claim, the claim is returned
 * unchanged. That is exactly the behaviour before formats existed, and it is
 * what a decimal page with a decimal request still gets: "around 1.80" reads
 * as 1.80 by either route.
 */
export function targetOddsInDecimal(
  text: string,
  claimed: number | null,
  pageFormat: OddsFormat,
): number | null {
  if (claimed === null || !Number.isFinite(claimed)) return claimed;

  for (const price of pricesWrittenIn(text, pageFormat)) {
    if (price.decimal !== null && refersTo(claimed, price)) return price.decimal;
  }

  return claimed;
}
